"use client";

import { useAuth } from "../lib/auth";
import { useVaultContext } from "../lib/vault";
import { useOutage } from "../lib/use-outage";
import { BANNERS } from "../lib/banner-registry";

// Shared banner mount. Every user-facing page renders <BannerSlot />
// in the same position. The banner registry (lib/banner-registry.js)
// decides which banner (if any) actually appears — filtered by each
// entry's `condition(profile, vaultCtx, outage)`, then sorted by priority
// ascending, first match wins.
//
// Returns null (no wrapper, no layout cost) when no banner matches.
//
// Individual banner components are still free to run their own auth /
// state check as defense-in-depth (LapsedBanner does this via useAuth,
// V2PermissionBanner does this via useVaultContext) — BannerSlot's
// registry filter is the primary gate, the banner's internal check is
// belt-and-suspenders.
//
// V2 Step 11a — extended condition signature to (profile, vaultCtx).
// Backwards-compat: existing profile-only conditions ignore the second
// arg. Vault-state-driven banners (V2PermissionBanner) use it.
//
// 2026-05-09 (#121-followup) — extended condition signature to
// (profile, vaultCtx, outage). OutageBanner condition reads
// outage.activeOutage from the OutageProvider; existing conditions
// ignore the third arg.

export default function BannerSlot() {
  const { profile } = useAuth();
  const vaultCtx = useVaultContext();
  const outage = useOutage();
  if (!profile) return null;

  const active = BANNERS
    .filter((b) => {
      try { return b.condition(profile, vaultCtx, outage); } catch { return false; }
    })
    .sort((a, b) => a.priority - b.priority);

  if (active.length === 0) return null;

  const winning = active[0];
  const Banner = winning.component;
  return <Banner profile={profile} banner={winning} />;
}
